/**
 * dsCoderRulesService — scope-aware CRUD for DS Coder user-authored rules stored
 * in public.ds_coder_rules. RLS enforces who can read/write each scope; this
 * layer only shapes rows and sets the owner columns for the active scope.
 */
import { supabase, getCurrentUserId } from "@/lib/supabase";
import type { DsCoderRule, DsCoderRuleInput, RuleScope } from "@/types/dsCoderRules";

const TABLE = "ds_coder_rules";
const SELECT =
  "id, scope, user_id, workspace_id, name, description, content, globs, always_apply, is_enabled, created_at, updated_at";

type Row = {
  id: string;
  scope: RuleScope;
  user_id: string | null;
  workspace_id: string | null;
  name: string;
  description: string | null;
  content: string | null;
  globs: string | null;
  always_apply: boolean;
  is_enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
};

function toApp(row: Row): DsCoderRule {
  return {
    id: row.id,
    scope: row.scope,
    userId: row.user_id,
    workspaceId: row.workspace_id,
    name: row.name,
    description: row.description ?? "",
    content: row.content ?? "",
    globs: row.globs ?? "",
    alwaysApply: row.always_apply,
    isEnabled: row.is_enabled,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Every rule the current user can apply for a chat: global defaults, their own
 * personal rules, and (when a workspace is active) that workspace's rules. RLS
 * guarantees only permitted rows return; we still filter workspace rows to the
 * active workspace client-side.
 */
export async function listResolvableRules(workspaceId?: string | null): Promise<DsCoderRule[]> {
  const scopes: RuleScope[] = workspaceId ? ["global", "user", "workspace"] : ["global", "user"];
  const { data, error } = await supabase.from(TABLE).select(SELECT).in("scope", scopes);
  if (error) throw new Error(error.message);
  return (data as Row[] | null ?? [])
    .filter((r) => (r.scope === "workspace" ? r.workspace_id === workspaceId : true))
    .map(toApp);
}

/** Rules for a single scope, for the management UI. */
export async function listRules(params: {
  scope: RuleScope;
  workspaceId?: string | null;
}): Promise<DsCoderRule[]> {
  let query = supabase.from(TABLE).select(SELECT).eq("scope", params.scope);
  if (params.scope === "workspace") {
    if (!params.workspaceId) return [];
    query = query.eq("workspace_id", params.workspaceId);
  } else if (params.scope === "user") {
    const userId = await getCurrentUserId();
    if (!userId) return [];
    query = query.eq("user_id", userId);
  }
  const { data, error } = await query.order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return (data as Row[] | null ?? []).map(toApp);
}

export async function createRule(params: {
  scope: RuleScope;
  workspaceId?: string | null;
  input: DsCoderRuleInput;
}): Promise<DsCoderRule> {
  const { scope, workspaceId, input } = params;
  const userId = scope === "user" ? await getCurrentUserId() : null;
  if (scope === "user" && !userId) throw new Error("Not authenticated");
  if (scope === "workspace" && !workspaceId) throw new Error("A workspace must be selected");

  const row = {
    scope,
    user_id: scope === "user" ? userId : null,
    workspace_id: scope === "workspace" ? workspaceId : null,
    name: input.name.trim(),
    description: input.description?.trim() ?? "",
    content: input.content ?? "",
    globs: input.globs?.trim() ?? "",
    always_apply: input.alwaysApply ?? false,
    is_enabled: input.isEnabled ?? true,
  };
  const { data, error } = await supabase.from(TABLE).insert(row).select(SELECT).single();
  if (error) throw new Error(error.message);
  return toApp(data as Row);
}

export async function updateRule(
  id: string,
  patch: Partial<DsCoderRuleInput>
): Promise<DsCoderRule> {
  const dbPatch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) dbPatch.name = patch.name.trim();
  if (patch.description !== undefined) dbPatch.description = patch.description.trim();
  if (patch.content !== undefined) dbPatch.content = patch.content;
  if (patch.globs !== undefined) dbPatch.globs = patch.globs.trim();
  if (patch.alwaysApply !== undefined) dbPatch.always_apply = patch.alwaysApply;
  if (patch.isEnabled !== undefined) dbPatch.is_enabled = patch.isEnabled;

  const { data, error } = await supabase
    .from(TABLE)
    .update(dbPatch)
    .eq("id", id)
    .select(SELECT)
    .single();
  if (error) throw new Error(error.message);
  return toApp(data as Row);
}

export async function deleteRule(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw new Error(error.message);
}
